import { useState } from "react";
import { toast } from "sonner";
import { Mail, Youtube, Send } from "lucide-react";
import PageHero from "@/components/PageHero";
import { FadeIn, ChapterLabel } from "@/components/Reveal";
import { YOUTUBE_URL } from "@/data/content";
import useSEO from "@/hooks/useSEO";

export default function Contact() {
  useSEO(
    "Contact | The Cap Chronicles",
    "Questions about The Cap Chronicles picture books, read-aloud videos, school visits, or classroom resources for ages 4-8? Write to us."
  );
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const submit = (e) => {
    e.preventDefault();
    toast.success("Thank you for writing. Your letter is on its way.");
    setForm({ name: "", email: "", message: "" });
  };

  const field = "w-full rounded-2xl border border-charcoal/15 bg-white px-5 py-4 text-charcoal placeholder:text-charcoal/40 focus:border-forest focus:outline-none transition-colors duration-300";

  return (
    <main>
      <PageHero
        kicker="Contact"
        title="Send a letter"
        italic="to the Vale."
        description="Questions, kind words, classroom enquiries, or your own story, and we read everything, usually with tea."
      />

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-12 md:pb-32" data-testid="contact-section">
        <div className="grid gap-16 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <ChapterLabel num="01" title="Write to Us" />
            <FadeIn delay={0.1}>
              <form onSubmit={submit} className="flex flex-col gap-4" data-testid="contact-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="contact-name-input" className={field} />
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="your@email.com" data-testid="contact-email-input" className={field} />
                </div>
                <textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Dear Cap Chronicles…" data-testid="contact-message-input" className={`${field} resize-none`} />
                <button type="submit" data-testid="contact-submit-button" className="group mt-2 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-forest text-sm font-semibold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-[1.02] sm:w-auto sm:px-10">
                  Send the Letter <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                </button>
              </form>
            </FadeIn>
          </div>

          <div>
            <ChapterLabel num="02" title="Other Paths" />
            <FadeIn delay={0.15} className="flex flex-col gap-4">
              <a href="mailto:hello@thecapchronicles.com" data-testid="contact-email-link" className="group flex items-center gap-4 rounded-3xl border border-charcoal/10 bg-white p-6 transition-shadow duration-500 hover:shadow-[0_25px_50px_-25px_rgba(47,107,60,0.3)]">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gold/20 text-charcoal"><Mail size={19} /></span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal/50">Email</p>
                  <p className="font-serif text-lg">hello@thecapchronicles.com</p>
                </div>
              </a>
              <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-youtube-link" className="group flex items-center gap-4 rounded-3xl border border-charcoal/10 bg-white p-6 transition-shadow duration-500 hover:shadow-[0_25px_50px_-25px_rgba(47,107,60,0.3)]">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-sky/15 text-sky"><Youtube size={19} /></span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal/50">YouTube</p>
                  <p className="font-serif text-lg">@TheCapChroniclesTV</p>
                </div>
              </a>
              <div className="rounded-3xl bg-forest/5 p-6">
                <p className="font-serif text-lg italic leading-relaxed text-forest">
                  “We read every letter out loud at story-time. Expect a reply within a few days. Some journeys shouldn't be rushed.”
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </main>
  );
}
